import { useState, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowRight, Calendar, Dna, Globe, Users, HeartHandshake, Stethoscope } from "lucide-react";
import PageHero from "./PageHero";

const whyItems = [
  { icon: Dna, title: "Starannie dobrane skojarzenia", desc: "Planujemy każdy miot z naciskiem na zdrowie, charakter i eksterier" },
  { icon: Globe, title: "Reproduktory z Niemiec", desc: "Współpracujemy z najlepszymi liniami hodowlanymi w Europie" },
  { icon: Users, title: "Socjalizacja szczeniąt", desc: "Kontakt z ludźmi, dziećmi i różnymi bodźcami od pierwszych dni" },
  { icon: HeartHandshake, title: "Wsparcie dla właścicieli", desc: "Wyprawka, zalecenia żywieniowe i pomoc przez cały pierwszy rok" },
  { icon: Stethoscope, title: "Kontrola weterynaryjna", desc: "Każdy miot jest badany przez inspektora weterynaryjnego" },
];

const KennelPuppies = () => {
  const { API } = useOutletContext();
  const [litters, setLitters] = useState([]);

  useEffect(() => {
    fetch(`${API}/litters`).then(r => r.json()).then(setLitters);
  }, [API]);

  return (
    <div>
      {/* Black Banner */}
      <PageHero
        breadcrumbs={[{ label: "SZCZENIĘTA" }]}
        label="SZCZENIĘTA"
        title="Szczenięta wychowywane z bliskością i troską"
        description="Od pierwszych chwil życia nasze szczenięta otoczone są ciepłem, kontaktem z ludźmi i różnorodnymi bodźcami. Socjalizacja zaczyna się od pierwszych dni — dzięki temu trafiają do nowych domów przygotowane, ufne i ciekawe świata."
        testId="puppies"
      />

      {/* Description */}
      <section className="bg-[#141416] px-6 md:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-xl text-[#F5F5F5] mb-5">Nasze szczenięta</h2>
          <p className="text-[#B8B8B8] text-sm leading-relaxed mb-3">
            Każdy miot w naszej hodowli jest starannie planowany. Szczenięta wychowują się w domowej atmosferze, mają kontakt z ludźmi i dziećmi oraz przechodzą odpowiednią socjalizację. Bardzo zależy nam, aby trafiały do odpowiedzialnych i kochających domów.
          </p>
          <p className="text-[#B8B8B8] text-sm leading-relaxed">
            Nowi właściciele otrzymują od nas wyprawkę, zalecenia żywieniowe oraz pełne wsparcie w pierwszym roku życia psa.
          </p>
        </div>
      </section>

      {/* Litters Gallery */}
      <div className="px-6 md:px-12 py-12 max-w-7xl mx-auto space-y-16">
        {litters.map((l, li) => {
          const photos = l.puppy_photos || [];
          const birthDisplay = l.birth_date ? new Date(l.birth_date).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }) : "";
          return (
            <motion.div key={l.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: li * 0.1 }} data-testid={`puppies-litter-${l.slug || l.id}`}>
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h2 className="font-serif text-2xl text-[#F5F5F5]">{l.name}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    {birthDisplay && <span className="text-[10px] tracking-[0.15em] text-[#D4AF37] flex items-center gap-1"><Calendar className="w-3 h-3" /> {birthDisplay}</span>}
                    {l.puppies_info && <span className="text-[#B8B8B8]/40 text-xs">{l.puppies_info}</span>}
                  </div>
                  <p className="text-[#B8B8B8]/50 text-xs mt-1 flex items-center gap-1">
                    <Heart className="w-3 h-3 text-[#D4AF37]/40" />
                    {l.mother?.name || "—"} × {l.father?.name || "—"}
                  </p>
                </div>
                <Link to={`/mioty/${l.slug || l.id}`}
                  className="text-xs tracking-[0.15em] text-[#D4AF37] border border-[#D4AF37]/20 px-6 py-2.5 hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 flex items-center gap-2"
                >ZOBACZ {l.name.toUpperCase()} <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>

              {photos.length > 0 ? (
                <div className="px-6 md:px-12 max-w-7xl mx-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {photos.map((photo, idx) => (
                    <Link key={idx} to={`/mioty/${l.slug || l.id}`}
                      className="block relative aspect-square overflow-hidden bg-[#0B0B0D] group"
                    >
                      <img src={photo} alt={`Szczenię ${idx + 1}`}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                        className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                    </Link>
                  ))}
                </div>
                </div>
              ) : (
                <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-12 text-center">
                  <Heart className="w-8 h-8 text-[#D4AF37]/20 mx-auto mb-3" />
                  <p className="text-[#B8B8B8]/30 text-sm">Zdjęcia wkrótce</p>
                </div>
              )}
            </motion.div>
          );
        })}

        {litters.length === 0 && (
          <div className="text-center py-24">
            <Heart className="w-10 h-10 text-[#D4AF37]/20 mx-auto mb-5" />
            <p className="text-[#F5F5F5] text-lg font-serif mb-3">Aktualnie nie mamy dostępnych szczeniąt</p>
            <p className="text-[#B8B8B8]/40 text-sm mb-10">Sprawdź nasze plany hodowlane lub skontaktuj się z nami.</p>
            <div className="flex gap-4 justify-center">
              <Link to="/mioty" className="px-8 py-3 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all">MIOTY</Link>
              <Link to="/kontakt" className="px-8 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all">KONTAKT</Link>
            </div>
          </div>
        )}
      </div>

      {/* Dlaczego nasze szczenięta — diagram */}
      <section className="bg-[#141416] border-t border-[#2A2A2D] px-6 md:px-12 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">CO NAS WYRÓŻNIA</span>
            <h2 className="font-serif text-[clamp(1.6rem,3vw,2.4rem)] text-[#F5F5F5]">Dlaczego nasze szczenięta?</h2>
            <div className="w-12 h-px bg-[#D4AF37]/40 mx-auto mt-5" />
          </div>

          {/* Timeline diagram */}
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent" />

            <div className="space-y-8 md:space-y-0">
              {whyItems.map((item, i) => {
                const Icon = item.icon;
                const isLeft = i % 2 === 0;
                return (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className={`md:flex items-center md:py-6 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    {/* Content card */}
                    <div className={`md:w-[calc(50%-2rem)] ${isLeft ? 'md:text-right md:pr-0' : 'md:text-left md:pl-0'}`}>
                      <div className={`bg-[#1A1A1D] border border-[#2A2A2D] p-6 inline-block w-full hover:border-[#D4AF37]/30 transition-colors duration-300`}>
                        <div className={`flex items-center gap-4 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-[#D4AF37]" />
                          </div>
                          <div className={isLeft ? 'md:text-right' : ''}>
                            <h3 className="text-[#F5F5F5] text-sm font-medium mb-1">{item.title}</h3>
                            <p className="text-[#B8B8B8]/60 text-xs leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Center dot */}
                    <div className="hidden md:flex w-16 justify-center flex-shrink-0">
                      <div className="w-3 h-3 rounded-full bg-[#D4AF37] border-2 border-[#141416] shadow-[0_0_8px_rgba(212,175,55,0.4)]" />
                    </div>

                    {/* Spacer */}
                    <div className="hidden md:block md:w-[calc(50%-2rem)]" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <div className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
        <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-[10px] tracking-[0.2em] text-[#D4AF37] mb-2 font-medium">ZAINTERESOWANY?</h3>
            <p className="text-[#B8B8B8] text-sm">Skontaktuj się z nami w sprawie rezerwacji szczeniąt.</p>
          </div>
          <Link to="/kontakt" className="px-8 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all flex-shrink-0">
            ZAPYTAJ O SZCZENIĘTA
          </Link>
        </div>
      </div>
    </div>
  );
};
export default KennelPuppies;
